import { Injectable } from '@angular/core';
import { PasswordValidators } from '../validators/password.validators';
import {
  Validators,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  AbstractControl,
  ValidatorFn,
} from '@angular/forms';
import { PhoneValidators } from '../validators/phone.validators';

interface ValidatorOptions {
  required?: boolean;
  requiredCheck?: boolean;
  maxLength?: number;
  minLength?: number;
  max?: number;
  min?: number;
  email?: boolean;
  password?: boolean;
  captcha?: string;
  phoneValid?: boolean;
  passwordMatch?: AbstractControl;
}

@Injectable({
  providedIn: 'root',
})
export class ValidatorService {
  /**
   * Validator for Email validation
   *
   * @param required boolean
   * @returns
   */
  emailValidator(required = false) {
    if (required) {
      return this.validateOn({
        required,
        email: true,
      });
    }

    return this.validateOn({
      email: true,
    });
  }

  /**
   * Validator for password validation
   *
   * @param required boolean
   * @returns
   */
  passwordValidator(required = false, strong = true, matchWith: AbstractControl = null) {
    if (required) {
      return this.validateOn({
        required,
        password: strong,
        passwordMatch: matchWith,
      });
    }

    return this.validateOn({
      password: strong,
      passwordMatch: matchWith,
    });
  }

  validateOn(options: ValidatorOptions) {
    const {
      required,
      requiredCheck,
      maxLength,
      minLength,
      max,
      min,
      email,
      password,
      passwordMatch,
      captcha,
      phoneValid,
    } = options;

    const validators = [];

    if (required) {
      validators.push(Validators.required);
    }

    if (requiredCheck) {
      validators.push(Validators.requiredTrue);
    }

    if (typeof maxLength === 'number') {
      validators.push(Validators.maxLength(maxLength));
    }

    if (typeof minLength === 'number') {
      validators.push(Validators.minLength(minLength));
    }

    if (max) {
      validators.push(Validators.max(max));
    }

    if (min) {
      validators.push(Validators.min(min));
    }

    if (email) {
      validators.push(Validators.email);
    }

    if (password) {
      validators.push(PasswordValidators.strong);
    }

    if (passwordMatch) {
      validators.push(PasswordValidators.passwordMatch(passwordMatch));
    }

    if (captcha) {
      validators.push(Validators.pattern(captcha));
    }

    if (phoneValid) {
      validators.push(PhoneValidators.phoneValid);
    }

    return validators || [];
  }

  /**
   * Set validators
   *
   * @param formControl
   * @param validator
   */
  setValidator(
    formControl: FormControl | AbstractControl,
    validator: ((control: AbstractControl) => ValidationErrors | null)[] | ValidatorFn | null
  ) {
    if (formControl) {
      formControl?.setValidators(validator);
      formControl?.updateValueAndValidity({ onlySelf: true });
    }
  }

  /**
   * Clear validators
   *
   * @param formControl
   */
  clearValidators(formControl: FormControl) {
    if (formControl) {
      formControl?.clearValidators();
      formControl?.updateValueAndValidity({ onlySelf: true });
    }
  }

  /**
   * Validate all forms of a formGroup
   *
   * @param formGroup
   */
  validateAllFormFields(formGroup: FormGroup | FormArray | undefined) {
    if (formGroup) {
      Object.keys(formGroup.controls).forEach((field) => {
        const control = formGroup.get(field);
        if (control instanceof FormControl) {
          control.markAsTouched({ onlySelf: true });
        } else if (control instanceof FormGroup || control instanceof FormArray) {
          this.validateAllFormFields(control);
        }
      });
    }
  }

  /**
   * Get all errors present in a formGroup
   *
   * @param formGroup the formGroup to get errors from
   * @returns
   */
  getFormValidationErrors(formGroup: FormGroup) {
    if (formGroup) {
      const errors: { [x: string]: { [x: string]: any } }[] = [];
      Object.keys(formGroup.controls).forEach((control) => {
        if (formGroup.get(control)) {
          const controlErrors: ValidationErrors | null | undefined = formGroup.get(control)?.errors;
          if (controlErrors != null) {
            Object.keys(controlErrors).forEach((keyError) => {
              errors.push({
                [control]: {
                  [keyError]: controlErrors[keyError],
                },
              });
            });
          }
        }
      });
      return errors;
    }

    return null;
  }
}
